import React, { useState, useEffect } from "react";
import { Card, Row, Col, Badge, Form } from "react-bootstrap";
import { motion, AnimatePresence } from "framer-motion";

import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import ExtraSettingsFieldTypeEnum from "./ExtraSettingsFieldTypeEnum";
import IconLoader from "components/IconLoader/IconLoader";

function ExtraSettings({
  settings,
  handleExtraSettingsToggle,
  handleFieldChange: externalHandleFieldChange
}) {
  const [localSettings, setLocalSettings] = useState(settings);

  useEffect(() => {
    setLocalSettings(settings);
  }, [settings]);

  const handleToggle = (index, checked) => {
    handleExtraSettingsToggle(index, checked);
  };

  const handleFieldChange = (settingIndex, fieldIndex, value) => {
    if (typeof externalHandleFieldChange === "function") {
      externalHandleFieldChange(settingIndex, fieldIndex, value);
    } else {
      const updatedSettings = { ...localSettings };
      if (updatedSettings?.extraSettings?.[settingIndex]?.fields?.[fieldIndex]) {
        updatedSettings.extraSettings[settingIndex].fields[fieldIndex].value = value;
        setLocalSettings(updatedSettings);
      }
    }
  };

  const cardVariants = {
    initial: { scale: 1 },
    hover: { scale: 1.02, boxShadow: "0 10px 20px rgba(0,0,0,0.1)" },
    tap: { scale: 0.98 }
  };

  // Create pairs of settings
  const getPairedSettings = () => {
    if (!localSettings?.extraSettings?.length) return [];

    const pairs = [];
    for (let i = 0; i < localSettings.extraSettings.length; i += 2) {
      pairs.push(localSettings.extraSettings.slice(i, i + 2));
    }
    return pairs;
  };

  // Render field based on type
  const renderField = (field, settingIndex, fieldIndex) => {
    switch (field.type) {
      case ExtraSettingsFieldTypeEnum.TIME:
        return (
          <DatePicker
            selected={field.value ? new Date(field.value) : null}
            onChange={(date) => handleFieldChange(settingIndex, fieldIndex, date)}
            showTimeSelect
            showTimeSelectOnly
            timeIntervals={15}
            timeCaption="Saat"
            dateFormat="HH:mm"
            className="form-control"
            placeholderText="Saat seçiniz"
            popperPlacement="bottom"
            popperModifiers={[
              {
                name: "preventOverflow",
                options: {
                  boundary: document.body
                }
              }
            ]}
          />
        );
      case ExtraSettingsFieldTypeEnum.DATE:
        return (
          <DatePicker
            selected={field.value ? new Date(field.value) : null}
            onChange={(date) => handleFieldChange(settingIndex, fieldIndex, date)}
            dateFormat="dd/MM/yyyy"
            className="form-control"
            placeholderText="Tarih seçiniz"
            popperPlacement="bottom"
            popperModifiers={[
              {
                name: "preventOverflow",
                options: {
                  boundary: document.body
                }
              }
            ]}
          />
        );
      case ExtraSettingsFieldTypeEnum.NUMBER:
        return (
          <Form.Control
            type="number"
            value={field.value || ""}
            onChange={(e) => handleFieldChange(settingIndex, fieldIndex, e.target.value)}
            placeholder="Sayı giriniz"
          />
        );
      case ExtraSettingsFieldTypeEnum.CHECKBOX:
        return (
          <Form.Check
            type="checkbox"
            checked={field.value || false}
            onChange={(e) => handleFieldChange(settingIndex, fieldIndex, e.target.checked)}
            label={field.label || ""}
          />
        );
      case ExtraSettingsFieldTypeEnum.SELECT:
        return (
          <Form.Select
            value={field.value || ""}
            onChange={(e) => handleFieldChange(settingIndex, fieldIndex, e.target.value)}
          >
            <option value="">Seçiniz</option>
            {field.options?.map((option, i) => (
              <option key={i} value={option.value}>
                {option.label}
              </option>
            ))}
          </Form.Select>
        );
      case ExtraSettingsFieldTypeEnum.TEXTAREA:
        return (
          <Form.Control
            as="textarea"
            rows={3}
            value={field.value || ""}
            onChange={(e) => handleFieldChange(settingIndex, fieldIndex, e.target.value)}
            placeholder="Metin giriniz"
          />
        );
      case ExtraSettingsFieldTypeEnum.EMAIL:
        return (
          <Form.Control
            type="email"
            value={field.value || ""}
            onChange={(e) => handleFieldChange(settingIndex, fieldIndex, e.target.value)}
            placeholder="E-posta giriniz"
          />
        );
      case ExtraSettingsFieldTypeEnum.PHONE:
        return (
          <Form.Control
            type="tel"
            value={field.value || ""}
            onChange={(e) => handleFieldChange(settingIndex, fieldIndex, e.target.value)}
            placeholder="Telefon numarası giriniz"
          />
        );
      case ExtraSettingsFieldTypeEnum.URL:
        return (
          <Form.Control
            type="url"
            value={field.value || ""}
            onChange={(e) => handleFieldChange(settingIndex, fieldIndex, e.target.value)}
            placeholder="URL giriniz"
          />
        );
      case ExtraSettingsFieldTypeEnum.COLOR:
        return (
          <Form.Control
            type="color"
            value={field.value || "#000000"}
            onChange={(e) => handleFieldChange(settingIndex, fieldIndex, e.target.value)}
          />
        );
      case ExtraSettingsFieldTypeEnum.RANGE:
        return (
          <Form.Range
            value={field.value || 0}
            onChange={(e) => handleFieldChange(settingIndex, fieldIndex, e.target.value)}
            min={field.min || 0}
            max={field.max || 100}
            step={field.step || 1}
          />
        );
      default:
        return (
          <Form.Control
            type="text"
            value={field.value || ""}
            onChange={(e) => handleFieldChange(settingIndex, fieldIndex, e.target.value)}
            placeholder="Değer giriniz"
          />
        );
    }
  };

  // Animation variants for field content
  const fieldsVariants = {
    hidden: {
      opacity: 0,
      height: 0,
      overflow: "hidden"
    },
    visible: {
      opacity: 1,
      height: "auto",
      transition: {
        height: { duration: 0.3 },
        opacity: { duration: 0.3, delay: 0.1 }
      }
    },
    exit: {
      opacity: 0,
      height: 0,
      transition: {
        height: { duration: 0.2 },
        opacity: { duration: 0.1 }
      }
    }
  };

  return (
    <motion.div
      initial={{ y: 40, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ delay: 0.3 }}
      style={{ position: "relative", zIndex: 1 }}
    >
      <motion.div
        variants={cardVariants}
        initial="initial"
        whileHover="hover"
        style={{ position: "relative", zIndex: 1 }}
      >
        <Card className="shadow-sm mb-0">
          <Card.Header className="bg-white py-3">
            <h5 className="mb-0">
              <IconLoader icon="mdiCog" className="mr-2 text-primary" /> Ek Ayarlar
            </h5>
          </Card.Header>
          <Card.Body>
            {localSettings?.extraSettings && localSettings.extraSettings.length > 0 ? (
              getPairedSettings().map((pair, pairIndex) => (
                <Row key={`pair-${pairIndex}`} className="mb-4 align-items-start">
                  {pair.map((setting, index) => {
                    const actualIndex = pairIndex * 2 + index;
                    return (
                      <Col key={setting.key} xs={12} md={6} className="mb-3 mb-md-0">
                        <motion.div
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          transition={{ delay: 0.1 * actualIndex }}
                          className="border rounded p-3"
                          style={{ position: "relative", zIndex: 1 }}
                        >
                          <div className="d-flex justify-content-between align-items-center mb-3">
                            <div>
                              <h6 className="fw-bold mb-1">{setting.value}</h6>
                              <p className="text-muted small mb-0">{setting.key}</p>
                            </div>
                            <div
                              onClick={() => handleToggle(actualIndex, !setting.enabled)}
                              className="d-flex justify-content-end align-items-center cursor-pointer"
                            >
                              <Badge
                                bg={setting.enabled ? "success" : "secondary"}
                                pill
                                className={`px-3 py-2 ${setting.enabled && "text-white"}`}
                              >
                                {setting.enabled ? "Aktif" : "Pasif"}
                              </Badge>
                            </div>
                          </div>

                          {/* Fields Section */}
                          <AnimatePresence>
                            {setting.enabled && setting.fields && setting.fields.length > 0 && (
                              <motion.div
                                className="mt-3"
                                key={`fields-${actualIndex}`}
                                variants={fieldsVariants}
                                initial="hidden"
                                animate="visible"
                                exit="exit"
                              >
                                {setting.fields.map((field, fieldIndex) => (
                                  <div key={`field-${fieldIndex}`} className="mb-2">
                                    <Form.Group>
                                      <Form.Label className="small fw-bold">
                                        {field.label}
                                      </Form.Label>
                                      {renderField(field, actualIndex, fieldIndex)}
                                    </Form.Group>
                                  </div>
                                ))}
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </motion.div>
                      </Col>
                    );
                  })}
                </Row>
              ))
            ) : (
              <div className="text-center py-4">
                <p className="text-muted">Herhangi bir ek ayar tanımlanmamış.</p>
              </div>
            )}
          </Card.Body>
        </Card>
      </motion.div>
    </motion.div>
  );
}

export default ExtraSettings;
